package dev.lingvo.site.pages

import kotlinx.serialization.json.Json
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.jsonArray
import kotlinx.serialization.json.jsonObject
import kotlinx.serialization.json.jsonPrimitive

/** One App Store storefront and the language its customers read the listing in. */
internal data class Storefront(val country: String, val name: String, val language: String)

/** What Apple returned for one storefront. Only the two fields the audit compares. */
internal data class StorefrontListing(val title: String, val description: String)

internal enum class AuditVerdict {
    /** The storefront shows listing text that differs from the reference: someone localized it. */
    LOCALIZED,

    /** The storefront shows the same text as the reference listing, in a language its customers may not read. */
    SAME_AS_REFERENCE,

    /** The app is not on sale in this storefront. */
    NOT_AVAILABLE,

    /** Apple did not answer; unknown, not counted either way. */
    FAILED,
}

internal data class AuditRow(val storefront: Storefront, val verdict: AuditVerdict, val title: String? = null)

internal data class AuditSummary(val checked: Int, val localized: Int, val sameAsReference: Int, val notAvailable: Int, val failed: Int)

/**
 * Storefronts whose customers mostly do not read English, so an English listing there costs installs.
 * English-language storefronts are left out on purpose: a match there proves nothing.
 */
internal val AUDIT_STOREFRONTS: List<Storefront> = listOf(
    Storefront("de", "Germany", "de"), Storefront("fr", "France", "fr"), Storefront("es", "Spain", "es"),
    Storefront("it", "Italy", "it"), Storefront("jp", "Japan", "ja"), Storefront("kr", "South Korea", "ko"),
    Storefront("br", "Brazil", "pt"), Storefront("pt", "Portugal", "pt"), Storefront("ru", "Russia", "ru"),
    Storefront("cn", "China mainland", "zh"), Storefront("tw", "Taiwan", "zh"), Storefront("hk", "Hong Kong", "zh"),
    Storefront("nl", "Netherlands", "nl"), Storefront("se", "Sweden", "sv"), Storefront("no", "Norway", "nb"),
    Storefront("dk", "Denmark", "da"), Storefront("fi", "Finland", "fi"), Storefront("pl", "Poland", "pl"),
    Storefront("tr", "Turkey", "tr"), Storefront("sa", "Saudi Arabia", "ar"), Storefront("ae", "United Arab Emirates", "ar"),
    Storefront("il", "Israel", "he"), Storefront("in", "India", "hi"), Storefront("id", "Indonesia", "id"),
    Storefront("my", "Malaysia", "ms"), Storefront("th", "Thailand", "th"), Storefront("vn", "Vietnam", "vi"),
    Storefront("ua", "Ukraine", "uk"), Storefront("cz", "Czechia", "cs"), Storefront("gr", "Greece", "el"),
    Storefront("hu", "Hungary", "hu"), Storefront("ro", "Romania", "ro"), Storefront("sk", "Slovakia", "sk"),
    Storefront("hr", "Croatia", "hr"), Storefront("mx", "Mexico", "es"), Storefront("ar", "Argentina", "es"),
    Storefront("co", "Colombia", "es"), Storefront("cl", "Chile", "es"),
)

/** The reference storefront every other one is compared with. */
internal const val AUDIT_REFERENCE_COUNTRY = "us"

private val APP_ID_IN_URL = Regex("""/id(\d{3,20})(?:[/?#]|$)""")
private val DIGITS_ONLY = Regex("""^(?:id)?(\d{3,20})$""", RegexOption.IGNORE_CASE)

/**
 * The numeric app id from what a person pastes: an App Store link, `id123456789`, or just the number.
 * Anything else is null, so nothing but digits ever reaches the request.
 */
internal fun parseAppStoreId(input: String): String? {
    val text = input.trim()
    DIGITS_ONLY.find(text)?.let { return it.groupValues[1] }
    if (!text.contains("apple.com", ignoreCase = true)) return null
    return APP_ID_IN_URL.find(text)?.groupValues?.get(1)
}

internal fun lookupUrl(appId: String, country: String): String =
    "https://itunes.apple.com/lookup?id=$appId&country=$country"

/** The listing from Apple's lookup response, or null when the app is not in that storefront. */
internal fun parseLookup(body: String): StorefrontListing? {
    val root = runCatching { Json.parseToJsonElement(body).jsonObject }.getOrNull() ?: return null
    val first = root["results"]?.jsonArray?.firstOrNull()?.jsonObject ?: return null
    return StorefrontListing(
        title = first.text("trackName").orEmpty(),
        description = first.text("description").orEmpty(),
    )
}

private fun JsonObject.text(key: String): String? = this[key]?.jsonPrimitive?.contentOrNull

private fun squash(text: String): String = text.trim().replace(Regex("""\s+"""), " ")

/** [listing] null means not on sale; [failed] means Apple did not answer. */
internal fun classify(reference: StorefrontListing?, listing: StorefrontListing?, failed: Boolean = false): AuditVerdict = when {
    failed -> AuditVerdict.FAILED
    listing == null -> AuditVerdict.NOT_AVAILABLE
    reference == null -> AuditVerdict.LOCALIZED
    squash(listing.description) == squash(reference.description) -> AuditVerdict.SAME_AS_REFERENCE
    else -> AuditVerdict.LOCALIZED
}

internal fun summarize(rows: List<AuditRow>): AuditSummary = AuditSummary(
    checked = rows.size,
    localized = rows.count { it.verdict == AuditVerdict.LOCALIZED },
    sameAsReference = rows.count { it.verdict == AuditVerdict.SAME_AS_REFERENCE },
    notAvailable = rows.count { it.verdict == AuditVerdict.NOT_AVAILABLE },
    failed = rows.count { it.verdict == AuditVerdict.FAILED },
)
